import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, CreditCard, User, Calendar, DollarSign, FileText, CheckCircle, XCircle, Clock, AlertCircle } from 'lucide-react';
import axios from '@services/api/axios.instance';
import { toast } from 'react-hot-toast';

const SubscriptionDetails = () => {
  const { transactionId } = useParams();
  const navigate = useNavigate();
  const [transaction, setTransaction] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchTransactionDetails();
  }, [transactionId]);

  const fetchTransactionDetails = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`/admin/subscriptions/${transactionId}`);
      
      if (response.data.success) {
        setTransaction(response.data.data.transaction);
      }
    } catch (error) {
      console.error('Error fetching transaction details:', error);
      toast.error('Failed to fetch transaction details');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      success: { color: 'green', icon: CheckCircle, label: 'Success' },
      pending: { color: 'yellow', icon: Clock, label: 'Pending' },
      created: { color: 'blue', icon: Clock, label: 'Created' },
      authorized: { color: 'blue', icon: Clock, label: 'Authorized' },
      failed: { color: 'red', icon: XCircle, label: 'Failed' },
      refunded: { color: 'gray', icon: AlertCircle, label: 'Refunded' }
    };

    const config = statusConfig[status] || statusConfig.pending;
    const Icon = config.icon;

    return (
      <span className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium bg-${config.color}-100 text-${config.color}-800 border border-${config.color}-300`}>
        <Icon className="w-5 h-5" />
        {config.label}
      </span>
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatAmount = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2
    }).format(amount || 0);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 p-6 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          <p className="mt-4 text-gray-600">Loading transaction details...</p>
        </div>
      </div>
    );
  }

  if (!transaction) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="text-center py-12">
          <CreditCard className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-600 text-lg">Transaction not found</p>
          <button
            onClick={() => navigate('/admin/subscriptions')}
            className="mt-4 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Back to Subscription History
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="w-full max-w-6xl mx-auto">
        
        <div className="mb-6">
          <button
            onClick={() => navigate('/admin/subscriptions')}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4 transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            Back to Subscription History
          </button>
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
                <CreditCard className="w-8 h-8 text-blue-600" />
                Transaction Details
              </h1>
              <p className="text-gray-600 mt-1">Transaction ID: #{transaction.id}</p>
            </div>
            {getStatusBadge(transaction.status)}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          
          <div className="lg:col-span-2 space-y-6">
            
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <FileText className="w-5 h-5 text-blue-600" />
                Transaction Information
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-500">Transaction ID</label>
                  <p className="text-gray-900 font-medium mt-1">#{transaction.id}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">Transaction Type</label>
                  <p className="text-gray-900 font-medium mt-1 capitalize">{transaction.transactionType?.replace('_', ' ')}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">Amount</label>
                  <p className="text-gray-900 font-bold text-lg mt-1">{formatAmount(transaction.amount)}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">Currency</label>
                  <p className="text-gray-900 font-medium mt-1">{transaction.currency}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">Payment Method</label>
                  <p className="text-gray-900 font-medium mt-1 capitalize">{transaction.paymentMethod || 'N/A'}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">Transaction Date</label>
                  <p className="text-gray-900 font-medium mt-1">{formatDate(transaction.createdAt)}</p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <User className="w-5 h-5 text-blue-600" />
                Student Information
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-500">Name</label>
                  <p className="text-gray-900 font-medium mt-1">{transaction.studentName}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">Email</label>
                  <p className="text-gray-900 font-medium mt-1">{transaction.user?.email || 'N/A'}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">Phone</label>
                  <p className="text-gray-900 font-medium mt-1">{transaction.user?.phone || 'N/A'}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">User ID</label>
                  <p className="text-gray-900 font-medium mt-1">{transaction.userId}</p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <DollarSign className="w-5 h-5 text-blue-600" />
                Payment Gateway Details
              </h2>
              <div className="grid grid-cols-1 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-500">Razorpay Order ID</label>
                  <p className="text-gray-900 font-mono text-sm mt-1 bg-gray-50 p-2 rounded">{transaction.razorpayOrderId}</p>
                </div>
                {transaction.razorpayPaymentId && (
                  <div>
                    <label className="text-sm font-medium text-gray-500">Razorpay Payment ID</label>
                    <p className="text-gray-900 font-mono text-sm mt-1 bg-gray-50 p-2 rounded">{transaction.razorpayPaymentId}</p>
                  </div>
                )}
                {transaction.razorpaySignature && (
                  <div>
                    <label className="text-sm font-medium text-gray-500">Razorpay Signature</label>
                    <p className="text-gray-900 font-mono text-xs mt-1 bg-gray-50 p-2 rounded break-all">{transaction.razorpaySignature}</p>
                  </div>
                )}
                {transaction.failureReason && (
                  <div>
                    <label className="text-sm font-medium text-red-500">Failure Reason</label>
                    <p className="text-red-700 mt-1 bg-red-50 p-3 rounded">{transaction.failureReason}</p>
                  </div>
                )}
              </div>
            </div>

            {transaction.refundAmount && (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">Refund Information</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-gray-500">Refund Amount</label>
                    <p className="text-gray-900 font-bold text-lg mt-1">{formatAmount(transaction.refundAmount)}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">Refund Date</label>
                    <p className="text-gray-900 font-medium mt-1">{formatDate(transaction.refundedAt)}</p>
                  </div>
                  {transaction.refundReason && (
                    <div className="md:col-span-2">
                      <label className="text-sm font-medium text-gray-500">Refund Reason</label>
                      <p className="text-gray-900 mt-1">{transaction.refundReason}</p>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          <div className="space-y-6">
            
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Plan Details</h2>
              <div className="space-y-4">
                <div>
                  <label className="text-sm font-medium text-gray-500">Plan Name</label>
                  <p className="text-gray-900 font-bold text-lg mt-1">{transaction.plan?.name || 'N/A'}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">Plan Slug</label>
                  <p className="text-gray-900 font-medium mt-1">{transaction.plan?.slug || 'N/A'}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">Billing Cycle</label>
                  <p className="text-gray-900 font-medium mt-1 capitalize">{transaction.plan?.billingCycle?.replace('_', ' ') || 'N/A'}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">Plan Price</label>
                  <p className="text-gray-900 font-bold text-lg mt-1">{formatAmount(transaction.plan?.price)}</p>
                </div>
              </div>
            </div>

            {transaction.subscription && (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <Calendar className="w-5 h-5 text-blue-600" />
                  Subscription Details
                </h2>
                <div className="space-y-4">
                  <div>
                    <label className="text-sm font-medium text-gray-500">Subscription ID</label>
                    <p className="text-gray-900 font-medium mt-1">#{transaction.subscription.id}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">Status</label>
                    <p className="text-gray-900 font-medium mt-1 capitalize">{transaction.subscription.status}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">Started At</label>
                    <p className="text-gray-900 font-medium mt-1">{formatDate(transaction.subscription.startedAt)}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">Expires At</label>
                    <p className="text-gray-900 font-medium mt-1">{formatDate(transaction.subscription.expiresAt)}</p>
                  </div>
                  {transaction.subscription.cancelledAt && (
                    <>
                      <div>
                        <label className="text-sm font-medium text-gray-500">Cancelled At</label>
                        <p className="text-gray-900 font-medium mt-1">{formatDate(transaction.subscription.cancelledAt)}</p>
                      </div>
                      {transaction.subscription.cancellationReason && (
                        <div>
                          <label className="text-sm font-medium text-gray-500">Cancellation Reason</label>
                          <p className="text-gray-900 mt-1">{transaction.subscription.cancellationReason}</p>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            )}

            {transaction.metadata && Object.keys(transaction.metadata).length > 0 && (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">Additional Metadata</h2>
                <pre className="text-xs bg-gray-50 p-4 rounded overflow-auto">
                  {JSON.stringify(transaction.metadata, null, 2)}
                </pre>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SubscriptionDetails;
